import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, FileText, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";
import {
  TraffickingCaseForm,
  type TraffickingCaseRecord,
  type TraffickingCaseFormData,
} from "./TraffickingCaseForm";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TraffickingCasesListProps {
  personId: string;
}

export function TraffickingCasesList({ personId }: TraffickingCasesListProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingCase, setEditingCase] =
    useState<TraffickingCaseRecord | null>(null);

  const { data, isLoading } = useQuery<{ data: TraffickingCaseRecord[] }>({
    queryKey: ["traffickingCases", personId],
    queryFn: () =>
      api.get<{ data: TraffickingCaseRecord[] }>(
        `/clients/${personId}/trafficking`,
      ),
    enabled: !!personId,
  });

  const createMutation = useMutation({
    mutationFn: (body: TraffickingCaseFormData) =>
      api.post(`/clients/${personId}/trafficking`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["traffickingCases", personId],
      });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...body
    }: TraffickingCaseFormData & { id: number }) =>
      api.put(`/clients/${personId}/trafficking/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["traffickingCases", personId],
      });
      setEditingCase(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      api.delete(`/clients/${personId}/trafficking/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["traffickingCases", personId],
      });
    },
  });

  const cases = data?.data ?? [];

  function renderCaseDetails(raw: string | null) {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return (
        <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          {Object.entries(parsed).map(([key, value]) => (
            <div key={key} className="contents">
              <dt className="text-muted-foreground capitalize">
                {key.replace(/_/g, " ")}
              </dt>
              <dd className="font-medium">{String(value)}</dd>
            </div>
          ))}
        </dl>
      );
    } catch {
      return (
        <p className="mt-2 text-sm text-foreground whitespace-pre-wrap">
          {raw}
        </p>
      );
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Trafficking Cases</h3>
        <Button size="sm" className="gap-2" onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4" />
          Add Case
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="p-4">
            <TraffickingCaseForm
              onSubmit={(d) => createMutation.mutate(d)}
              onCancel={() => setShowForm(false)}
              isLoading={createMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {editingCase && (
        <Card>
          <CardContent className="p-4">
            <TraffickingCaseForm
              initial={editingCase}
              onSubmit={(d) =>
                updateMutation.mutate({ id: editingCase.id, ...d })
              }
              onCancel={() => setEditingCase(null)}
              isLoading={updateMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {!isLoading && cases.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No trafficking cases found.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading &&
        cases.map((c) => {
          const authorName =
            c.author_first_name && c.author_last_name
              ? `${c.author_first_name} ${c.author_last_name}`
              : null;

          return (
            <Card key={c.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-medium">
                      Trafficking Case #{c.id}
                    </CardTitle>
                    <CardDescription className="mt-0.5">
                      {new Date(c.created_at).toLocaleDateString()}
                      {authorName && ` | by ${authorName}`}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditingCase(c)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm("Delete this trafficking case?")) {
                          deleteMutation.mutate(c.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>{renderCaseDetails(c.case_details)}</CardContent>
            </Card>
          );
        })}
    </div>
  );
}
